import {KpisList, PlantKPIs, ProductionDataType} from '@domain/kpis'
import {DataContentWrapper} from '@hconnect/common/containers'
import {formatNumber} from '@hconnect/common/utils'
import {formatTimeZoneDate} from '@hconnect/uikit'
import {DataTable, Column, CardBox} from '@hconnect/uikit/src/lib2'
import {useUpmPlantsContext, useUpmPlantTimezone} from '@hconnect/upm'
import CloudOffIcon from '@mui/icons-material/CloudOff'
import {Box, Theme, Typography, useMediaQuery} from '@mui/material'
import {
  KPI_ICONS_MAP,
  DeltaLabel,
  kpiDeltaReversedColorMap,
  ActualKpiTemplates
} from '@widgets/kpis'
import {camelCase} from 'lodash'
import {useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {PlantKpiMobileGrids} from '../components'
import {FEATURE_NAMESPACE} from '../consts'
import {
  useAlternativeFuelRates,
  useKilnCoefficients,
  useKilnHeatConsumption,
  useMeanTimeBetweenFailures,
  useTechnicalCementPowerConsumption,
  usePlantStatusDateRange
} from '../hooks'
import {
  mapAlternativeFuelRatesDto,
  mapKilnCoefficientsDto,
  mapKilnHeatConsumptionsDto,
  mapMeanTimeBetweenFailuresDto,
  mapTechnicalCementPowerConsumptionDto
} from '../mappers'
import type {KpiTableData} from '../types'
import {generateKpiData, showYtmData} from '../utils'

const optionalKPIs = [
  KpisList.KilnReliabilityCoefficient,
  KpisList.KilnOperatingCoefficient,
  KpisList.KilnHeatConsumption,
  KpisList.AlternativeFuelRate,
  KpisList.MTBF,
  KpisList.TechnicalCementPowerCons
]

const ROW_SX = {
  td: {
    fontSize: 16,
    lineHeight: '40px'
  }
}

type PlantKpiDataProps = {
  plantId: string
  isGrindingUnit?: boolean
  productionData: {
    cementProduction: {data: ProductionDataType}
    clinkerProduction: {data: ProductionDataType}
  }
  showKPIs: boolean | '' | null | undefined
}

export const PlantKpiData: React.FC<PlantKpiDataProps> = ({
  plantId,
  isGrindingUnit = false,
  productionData,
  showKPIs
}) => {
  const {t, i18n} = useTranslation(FEATURE_NAMESPACE)
  const language = i18n.language
  const timezone = useUpmPlantTimezone(plantId)
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'))

  const upmPlants = useUpmPlantsContext()

  const {to, quickSelectionId} = usePlantStatusDateRange({timezone})
  const timestamp = to.toISOString()

  const showYTM = showYtmData(quickSelectionId)
  const isToday = quickSelectionId === 'today'

  const formattedDate = useMemo(
    () => formatTimeZoneDate(to, timezone, isToday ? 'DD/MM/YY' : 'MM/YY', language),
    [language, isToday, timezone, to]
  )

  // Note: KPI endpoints have a "fake" parent wrapper with no data and single descendant
  const {
    data: kilnCoefficientData,
    isLoading: isKilnCoefficientLoading,
    isError: isKilnCoefficientError
  } = useKilnCoefficients(
    {
      timezone,
      timestamp,
      locationId: plantId,
      showYTM
    },
    {
      enabled: !isGrindingUnit,
      select: (data) => mapKilnCoefficientsDto(data, upmPlants)
    }
  )

  const {
    data: technicalCementPowerConsumptionData,
    isLoading: isTechnicalCementPowerConsumptionLoading,
    isError: isTechnicalCementPowerConsumptionError
  } = useTechnicalCementPowerConsumption(
    {
      timezone,
      timestamp,
      locationId: plantId,
      showYTM
    },
    {select: (data) => mapTechnicalCementPowerConsumptionDto(data, upmPlants)}
  )

  const {
    data: kilnHeatConsumptionsData,
    isLoading: isKilnHeatConsumptionsLoading,
    isError: isKilnHeatConsumptionsError
  } = useKilnHeatConsumption(
    {
      timezone,
      timestamp,
      locationId: plantId,
      showYTM
    },
    {
      enabled: !isGrindingUnit,
      select: (data) => mapKilnHeatConsumptionsDto(data, upmPlants)
    }
  )

  const {
    data: meanTimeBetweenFailuresData,
    isLoading: isMeanTimeBetweenFailuresLoading,
    isError: isMeanTimeBetweenFailuresError
  } = useMeanTimeBetweenFailures(
    {
      timezone,
      timestamp,
      locationId: plantId,
      showYTM
    },
    {
      enabled: !isGrindingUnit,
      select: (data) => mapMeanTimeBetweenFailuresDto(data, upmPlants)
    }
  )

  const {
    data: alternativeFuelRatesData,
    isLoading: isAlternativeFuelRatesLoading,
    isError: isAlternativeFuelRatesError
  } = useAlternativeFuelRates(
    {
      timezone,
      timestamp,
      locationId: plantId,
      showYTM
    },
    {
      enabled: !isGrindingUnit,
      select: (data) => mapAlternativeFuelRatesDto(data, upmPlants)
    }
  )

  const KpiData: PlantKPIs = useMemo(
    () =>
      generateKpiData(
        plantId,
        {
          alternativeFuelRatesError: isAlternativeFuelRatesError,
          meanTimeBetweenFailuresError: isMeanTimeBetweenFailuresError,
          kilnCoefficientError: isKilnCoefficientError,
          kilnHeatConsumptionsError: isKilnHeatConsumptionsError,
          technicalCementPowerConsumptionError: isTechnicalCementPowerConsumptionError
        },
        {
          alternativeFuelRatesData,
          meanTimeBetweenFailuresData,
          kilnCoefficientData,
          kilnHeatConsumptionsData,
          technicalCementPowerConsumptionData
        }
      ),
    [
      alternativeFuelRatesData,
      isAlternativeFuelRatesError,
      kilnCoefficientData,
      isKilnCoefficientError,
      kilnHeatConsumptionsData,
      isKilnHeatConsumptionsError,
      meanTimeBetweenFailuresData,
      isMeanTimeBetweenFailuresError,
      plantId,
      technicalCementPowerConsumptionData,
      isTechnicalCementPowerConsumptionError
    ]
  )

  const kpiData = showKPIs ? KpiData : {}
  const AllData = {...productionData, ...kpiData}

  const kpiTableData: KpiTableData[] = Object.keys(AllData)
    .filter((kpi) => {
      if (optionalKPIs.includes(kpi as KpisList)) {
        const actualValue = AllData[kpi].data?.actual
        if (!actualValue) {
          return false
        }
      }

      if (isGrindingUnit)
        return (
          (kpi as KpisList) === KpisList.TechnicalCementPowerCons ||
          (kpi as KpisList) === KpisList.CementProduction
        )
      return true
    })
    .map((kpi) => ({
      name: kpi,
      ...AllData[kpi]
    }))

  const kpisTableConfig = useMemo<Column<KpiTableData>[]>(
    () => [
      {
        key: 'kpi',
        label: t('plantKpi.label.kpi'),
        customTemplate: ({name, data}) => (
          <Box display="flex" alignItems="center">
            <Box display="flex" mr={1} fontSize={14}>
              {KPI_ICONS_MAP[name]}
            </Box>
            <Typography variant="h5">
              {t(`actualVsPlanned.plantKpis.${camelCase(name as string)}`)}{' '}
              {data?.unit && `(${data.unit})`}
            </Typography>
          </Box>
        )
      },
      {
        key: 'planned',
        label: t('plantKpi.label.planned', {date: formattedDate}),
        customTemplate: ({data, isError}) =>
          isError || !data ? (
            <Box
              sx={({palette}) => ({
                display: 'flex',
                alignItems: 'center',
                whiteSpace: 'nowrap',
                fontStyle: 'italic',
                color: palette.text.secondary
              })}
            >
              <CloudOffIcon sx={{m: 1}} />
              <Typography variant="body1">{t('plantKpi.message.fetchingError')}</Typography>
            </Box>
          ) : (
            formatNumber(data.planned, language)
          )
      },
      {
        key: 'actual',
        label: t('plantKpi.label.actual', {date: formattedDate}),
        customTemplate: ({name, data, isError}) => {
          if (!isError && data) {
            const Template = ActualKpiTemplates[name]
            const {actual = 0, planned = 0} = data
            return <Template actual={formatNumber(actual, language)} diff={actual - planned} />
          }

          return <></>
        }
      },
      {
        key: 'delta',
        label: t('plantKpi.label.delta'),
        customTemplate: ({name, data, isError}) =>
          !isError && data ? (
            <DeltaLabel value={data.delta} reverseColor={kpiDeltaReversedColorMap[name]} />
          ) : (
            <></>
          )
      }
    ],
    [formattedDate, t, language]
  )

  if (isMobile) {
    return <PlantKpiMobileGrids kpiTableData={kpiTableData} formattedDate={formattedDate} />
  }

  return (
    <CardBox px={1.5} pb={0} data-test-id="plant-kpi-data">
      <Typography variant="h3" display="flex" alignItems="center" ml={1.5} mb={3}>
        {t('plantKpi.label.janusMonthlyKPIs')}
      </Typography>
      <DataContentWrapper<PlantKPIs>
        showLoader={
          isKilnCoefficientLoading ||
          isTechnicalCementPowerConsumptionLoading ||
          isMeanTimeBetweenFailuresLoading ||
          isAlternativeFuelRatesLoading ||
          isKilnHeatConsumptionsLoading
        }
        data={KpiData}
        isError={false}
        progressSx={{color: 'primary.main'}}
      >
        <DataTable<KpiTableData>
          columns={kpisTableConfig}
          data={kpiTableData || []}
          emptyMessage={t('plantKpi.message.noData')}
          rowSx={() => ROW_SX}
          keyExtractor={(item) => item.name}
        />
      </DataContentWrapper>
    </CardBox>
  )
}
